import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { crmApi, ApiError } from '@solara/api-client';

export default function LeadCreatePage() {
  const { t } = useTranslation('p1');
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    companyName: '',
    trn: '',
    industry: 'OTHER',
    primaryContactFirstName: '',
    primaryContactLastName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    leadSource: 'INBOUND_WEB',
    bantBudget: 'UNKNOWN',
    bantAuthority: 'UNKNOWN',
    bantNeed: 'UNKNOWN',
    bantTimeline: 'UNKNOWN',
    notes: '',
  });

  async function submit() {
    setError(null);
    setBusy(true);
    try {
      // assignedToUserId defaults to the JWT user on the backend if omitted.
      const lead = await crmApi.createLead(form);
      navigate(`/crm/leads/${lead.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <button onClick={() => navigate(-1)} className="text-xs text-fg-muted hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3 rtl-mirror" /> {t('actions.backToList')}
        </button>
        <h1 className="text-2xl font-semibold tracking-tight">{t('crm.newLead')}</h1>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('crm.companyName')}</CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
          <Input
            label={`${t('crm.companyName')} *`}
            value={form.companyName}
            onChange={(e) => setForm({ ...form, companyName: e.target.value })}
          />
          <Input
            label={t('crm.trn')}
            value={form.trn}
            onChange={(e) => setForm({ ...form, trn: e.target.value })}
            placeholder="15-digit UAE TRN"
          />
          <select
            className="rounded border border-border bg-surface px-3 py-2 text-sm"
            value={form.industry}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
          >
            {['Free Zone', 'Hospitality', 'Retail', 'Manufacturing', 'Services', 'Construction', 'Healthcare', 'Education', 'OTHER'].map((i) => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
          <select
            className="rounded border border-border bg-surface px-3 py-2 text-sm"
            value={form.leadSource}
            onChange={(e) => setForm({ ...form, leadSource: e.target.value })}
          >
            {['INBOUND_WEB', 'OUTBOUND_CALL', 'CONFERENCE', 'PARTNER_REFERRAL', 'MARKETING_CAMPAIGN', 'OTHER'].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('crm.primaryContact')}</CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
          <Input
            label={`${t('crm.contactFirstName')} *`}
            value={form.primaryContactFirstName}
            onChange={(e) => setForm({ ...form, primaryContactFirstName: e.target.value })}
          />
          <Input
            label={`${t('crm.contactLastName')} *`}
            value={form.primaryContactLastName}
            onChange={(e) => setForm({ ...form, primaryContactLastName: e.target.value })}
          />
          <Input
            label={`${t('crm.contactEmail')} *`}
            type="email"
            value={form.primaryContactEmail}
            onChange={(e) => setForm({ ...form, primaryContactEmail: e.target.value })}
          />
          <Input
            label={t('crm.contactPhone')}
            value={form.primaryContactPhone}
            onChange={(e) => setForm({ ...form, primaryContactPhone: e.target.value })}
            placeholder="+9715XXXXXXXX"
          />
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>BANT</CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
          <BantSelect label={t('crm.bantBudget')} value={form.bantBudget} onChange={(v) => setForm({ ...form, bantBudget: v })} options={['CONFIRMED', 'INDICATIVE', 'UNKNOWN', 'NONE']} />
          <BantSelect label={t('crm.bantAuthority')} value={form.bantAuthority} onChange={(v) => setForm({ ...form, bantAuthority: v })} options={['DECISION_MAKER', 'INFLUENCER', 'END_USER', 'UNKNOWN']} />
          <BantSelect label={t('crm.bantNeed')} value={form.bantNeed} onChange={(v) => setForm({ ...form, bantNeed: v })} options={['URGENT', 'MEDIUM', 'LOW', 'UNKNOWN']} />
          <BantSelect label={t('crm.bantTimeline')} value={form.bantTimeline} onChange={(v) => setForm({ ...form, bantTimeline: v })} options={['LT_3_MONTH', 'M3_6_MONTH', 'M6_12_MONTH', 'GT_12_MONTH', 'UNKNOWN']} />
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('crm.notes')}</CardTitle>
        </CardHeader>
        <CardBody>
          <textarea
            className="w-full min-h-[6rem] rounded border border-border bg-surface px-3 py-2 text-sm"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </CardBody>
      </Card>

      <div className="flex justify-end">
        <Button onClick={submit} disabled={busy}>
          {busy ? t('actions.saving') : t('crm.saveLead')}
        </Button>
      </div>
    </div>
  );
}

function BantSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-xs text-fg-muted">{label}</span>
      <select
        className="rounded border border-border bg-surface px-3 py-2 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}
